import React from 'react';
import { NavigateFunction } from 'react-router';

import { ColumnsConfigItem } from '@/components/table/sortableColumns/types';
import { Table } from '@/components/table/Table';
import { Columns, TableActions } from '@/components/table/types';
import { Filters } from '@/hooks/useAssets';
import { useEntitlements } from '@/hooks/useEntitlements';
import { useHasActiveFilters } from '@/hooks/useHasActiveFilters';
import { getAssetActionOptions } from '@/sections/asset/config/actionOptions';
import { AssetActionsHandlers } from '@/sections/asset/types/table.types';
import { getAssetNoDataConfig } from '@/sections/asset/utils/getAssetNoDataConfig';
import { useAuth } from '@/state/auth';
import { useBulkJobsStore } from '@/state/bulkJobs.store';
import { useGlobalState } from '@/state/global.state';
import { useOsintContext } from '@/state/OsintContext';
import { Asset } from '@/types';

import { CellFiltersProvider } from './cells/CellFiltersContext';

/**
 * Props for AssetTable component
 */
export interface AssetTableProps {
  // Asset data
  assets: Asset[];
  assetsStatus: 'pending' | 'error' | 'success';
  assetsError: Error | null;
  fetchNextPage: () => void;
  isFetching?: boolean;
  isFetchingNextPage: boolean;
  assetsHasNextPage: boolean;
  isAllEmpty: boolean;
  isQueryError: boolean;

  // Column configuration
  columns: Columns<Asset>;
  activeColumns: string[] | undefined;
  setActiveColumns: (columns: string[]) => void;
  placeholder: string;
  sortableColumnsConfig: {
    fixed: ColumnsConfigItem[];
    active: ColumnsConfigItem[];
    available: ColumnsConfigItem[];
  };

  // Selection (key-based: contains asset keys, not indices)
  selectedRows: string[];
  setSelectedRows: (rows: string[]) => void;

  // Filters and sort
  filters: Filters;
  setFilters: React.Dispatch<React.SetStateAction<Filters>>;

  // Actions and handlers
  handlers: AssetActionsHandlers;
  handleKeyboardAction: (code: string, assets: Asset[]) => void;
  setIsCommentModalOpen: (open: boolean) => void;
  setIsTagModalOpen: (open: boolean) => void;

  // Navigation and state
  navigate: NavigateFunction;
  resetQueryParams: () => void;
  drawerState: {
    handleOpenAssetDrawer: (
      asset?: Asset,
      isActiveDirectory?: boolean,
      osint?: boolean
    ) => void;
  };

  // Empty state callback
  /** Callback when "Get Started" button is clicked (first-time user onboarding) */
  onGetStartedClick?: () => void;

  // Drawer-specific props
  /** Table name for storage key (column preferences) */
  name?: string;
  /** Enable responsive column behavior (disabled in drawer context) */
  responsive?: boolean;
  /** Hide bulk actions and selection */
  hideBulkActions?: boolean;
}

/**
 * Asset Table Component
 *
 * Displays the main asset table with all interactive features including:
 * - Column configuration and sorting
 * - Row selection and bulk actions
 * - Keyboard shortcuts
 * - Pagination and infinite scrolling
 * - Error states and empty states
 * - Row actions menu
 *
 * @param props - Asset table props
 * @returns Asset table component
 */
export const AssetTable: React.FC<AssetTableProps> = ({
  assets,
  assetsStatus,
  assetsError,
  fetchNextPage,
  isFetching,
  isFetchingNextPage,
  assetsHasNextPage,
  isAllEmpty,
  isQueryError,
  columns,
  activeColumns,
  setActiveColumns,
  placeholder,
  sortableColumnsConfig,
  selectedRows,
  setSelectedRows,
  filters,
  setFilters,
  handlers,
  handleKeyboardAction,
  setIsCommentModalOpen,
  setIsTagModalOpen,
  navigate,
  resetQueryParams,
  drawerState,
  onGetStartedClick,
  name = 'assets',
  responsive = true,
  hideBulkActions = false,
}) => {
  const { isPraetorianUser } = useAuth();
  const isKeyPending = useBulkJobsStore(state => state.isKeyPending);
  const {
    modal: { selectUserAgent },
  } = useGlobalState();
  const { isOsint } = useOsintContext();
  const { canWriteAssets } = useEntitlements();

  // Determine if there are active filters (search or advancedFilters)
  const hasActiveFilters = useHasActiveFilters(filters);

  const noDataConfig = getAssetNoDataConfig({
    isQueryError,
    isAllEmpty,
    onResetQuery: resetQueryParams,
    navigate,
    hasActiveFilters,
    onClearFilters: resetQueryParams,
    onGetStartedClick,
  });

  return (
    <CellFiltersProvider value={filters}>
      <Table
        responsive={responsive}
        tableClassName="w-full [&_th:last-child]:sticky [&_th:last-child]:right-0 [&_td:last-child]:sticky [&_td:last-child]:right-0"
        sortableColumns={{
          activeColumns,
          setActiveColumns,
          defaultConfig: sortableColumnsConfig,
          placeholder,
        }}
        keyboardNavigation={true}
        selection={
          hideBulkActions
            ? { hide: true }
            : {
                value: selectedRows,
                onChange: setSelectedRows,
                selectAll: true,
              }
        }
        rowActions={
          isOsint || !canWriteAssets
            ? undefined // No row actions menu in OSINT mode or without write permission
            : (asset: Asset): TableActions => {
                return {
                  menu: {
                    items: getAssetActionOptions({
                      assets: asset ? [asset] : [],
                      onCommentClick: () => setIsCommentModalOpen(true),
                      onTagClick: () => {
                        // Select this asset by key if not already selected
                        if (!selectedRows.includes(asset.key)) {
                          setSelectedRows([asset.key]);
                        }
                        setIsTagModalOpen(true);
                      },
                      handlers,
                      selectUserAgent,
                      isPraetorianUser,
                    }).map(option => ({
                      ...option,
                      value: option.value?.toString(),
                    })),
                  },
                };
              }
        }
        onRowClick={
          isOsint
            ? undefined // No click handler in OSINT mode
            : asset => {
                drawerState.handleOpenAssetDrawer(asset, false, undefined);
              }
        }
        sort={{
          orderBy: filters.sort?.orderBy || 'identifier',
          descending: filters.sort?.descending || false,
          onSort: (sort: { orderBy: keyof Asset; descending: boolean }) => {
            setFilters(prev => ({ ...prev, sort }));
          },
        }}
        rowHeight={64}
        name={name}
        columns={columns}
        data={assets}
        rowClassName={(asset: Asset) =>
          isKeyPending(asset.key) ? 'opacity-70 cursor-wait' : ''
        }
        getRowProps={(asset: Asset) =>
          isKeyPending(asset.key) ? { 'aria-busy': true } : {}
        }
        error={assetsError}
        status={assetsStatus}
        isFetching={isFetching}
        fetchNextPage={fetchNextPage}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={assetsHasNextPage}
        noData={noDataConfig}
        keyboardActions={{
          altKey: true,
          codes: ['KeyF', 'KeyU', 'KeyD'],
          onKeyboardAction: handleKeyboardAction,
        }}
      />
    </CellFiltersProvider>
  );
};
